import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ArrowIcon } from '../../assets/icons/down-arrow.svg';
import { ReactComponent as SearchIcon } from '../../assets/icons/search-icon.svg';
import { ReactComponent as Search } from '../../assets/icons/search.svg';
import { ReactComponent as DownArrowIcon } from '../../assets/icons/up-arrow.svg';

interface Option {
  label: string;
  value: string;
}

interface MultiSelectDropdownProperties {
  name: string;
  options: Option[];
  selectedValues: string[];
  onSelectionChange: (name: string, values: string[]) => void;
  title?: string;
  placeholderFilled: string;
  placeholder?: string;
  icon?: JSX.Element;
  search?: boolean;
  multiSelectDisable?: boolean;
  fontSize?: 'small' | 'medium' | 'large';
  valueRequired?: boolean;
  error?: boolean;
  errorMessage?: string;
  version?: string;
  disabledAutoSorting?: boolean; // Optional prop to disable auto-sorting of options
  selectedChips?: boolean;
  limitChips?: number;
  disabled?: boolean;
}
/**
 * MultiSelectDropdown component provides a dropdown menu allowing users to select multiple options.
 *
 * This component is customizable and can be used in forms where multiple selections are required,
 * such as filtering items by categories or tags.
 * @param {object} props - The component props.
 * @param {string[]} props.options - The list of options available for selection.
 * @param {string[]} props.selectedValues - The values currently selected by the user.
 * @param {Function} props.onChange - Callback function triggered when the selected values change.
 * @returns {JSX.Element} The rendered multi-select dropdown component.
 */
function MultiSelectDropdown({
  options,
  selectedValues,
  name,
  onSelectionChange,
  title = 'Select Role',
  placeholderFilled,
  placeholder = 'Select Option',
  valueRequired,
  icon,
  multiSelectDisable,
  search,
  fontSize,
  error,
  errorMessage,
  version,
  disabledAutoSorting = false,
  selectedChips,
  limitChips,
  disabled,
}: MultiSelectDropdownProperties) {
  const reference = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('facilityManagement');
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [, setSelectValue] = useState<string>('');
  /**
   * Handles the change event for a checkbox in the multi-select dropdown.
   * It updates the selected values based on whether the checkbox is checked or unchecked.
   * @param {string} value - The value of the checkbox that was changed.
   */
  const handleCheckboxChange = (value: string) => {
    if (disabled) return;
    const newSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter((option) => option !== value)
      : [...selectedValues, value];
    onSelectionChange(name, newSelectedValues);
    setSelectValue(newSelectedValues.toString());
  };

  // 1. Search filter
  const filteredOptions = options.filter((option) =>
    option.label?.toLowerCase().includes(searchTerm.trim().toLowerCase()),
  );

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (reference.current && !reference.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  // 2. Apply sorting *only if auto-sorting is not disabled*
  const sortedOptions = disabledAutoSorting
    ? filteredOptions // If sorting is disabled → only show filtered list
    : [...filteredOptions].sort((a, b) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase(), undefined, { numeric: true }),
      ); // Sort alphabetically or numerically

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <>
      {version === 'v1' && (
        <div className={`multi-select-dropdown-v1 ${fontSize}`}>
          <div className="multi-select-header">
            <p>
              {title && (
                <p className={`input-label ${error && 'error'}`}>
                  {title}
                  {valueRequired && <span>*</span>}
                </p>
              )}
            </p>
          </div>
          <div className={`multi-select-dropdown-list ${isOpen ? 'open' : ''}`} ref={reference}>
            <div
              className={`dropdown-header ${error && 'error'}`}
              onClick={() => setIsOpen((previous) => !previous)}
              role="presentation">
              <div className="left-section">
                {icon}
                <div className="selected-chips">
                  {selectedChips && selectedValues.length > 0 ? (
                    <>
                      {selectedValues
                        .slice(0, limitChips && limitChips > 0 ? limitChips : selectedValues.length)
                        .map((value) => {
                          const selectedItem = options.find((opt) => opt.value === value);
                          return (
                            <span className="chip" key={value}>
                              {selectedItem?.label}
                              <button
                                type="button"
                                className="remove-chip"
                                onClick={(even) => {
                                  even.stopPropagation();
                                  handleCheckboxChange(value);
                                }}
                                aria-label={`Remove ${selectedItem?.label}`}>
                                ✖
                              </button>
                            </span>
                          );
                        })}
                      {limitChips && limitChips > 0 && selectedValues.length > limitChips && (
                        <span className="chip-count">
                          +{selectedValues.length - limitChips} more
                        </span>
                      )}
                    </>
                  ) : (
                    <span>
                      {selectedValues.length ? `${placeholderFilled} ${t('selected')}` : placeholder}
                    </span>
                  )}
                </div>{' '}
              </div>
              <span className={`dropdown-arrow `}>
                {isOpen ? <DownArrowIcon /> : <ArrowIcon />}
              </span>
            </div>
            {isOpen && (
              <div
                className={`dropdown-content ${isOpen ? 'open' : ''}`}
                style={{ borderTop: search ? 'none' : '' }}>
                {search && (
                  <div className="multi-select-search">
                    <input
                      type="text"
                      className="dropdown-search"
                      placeholder={t('search')}
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      // required={valueRequired}
                    />
                    <div className="search-box">
                      <span>
                        <Search />
                      </span>
                    </div>
                  </div>
                )}
                {isOpen && (
                  <div className={isOpen ? 'dropdown-list' : 'opened'}>
                    <div className="dropdown-lists">
                      {sortedOptions.length > 0 ? (
                        sortedOptions.map((option) => (
                          <span
                            key={option.value}
                            className={`slide-left ${disabled && 'disabled'}`}>
                            <input
                              style={{ display: multiSelectDisable ? 'none' : '' }}
                              type="checkbox"
                              checked={selectedValues.includes(option.value)}
                              onChange={() => handleCheckboxChange(option.value)}
                              disabled={disabled}
                            />
                            <p
                              role="presentation"
                              onClick={() => {
                                if (!disabled) handleCheckboxChange(option.value);
                              }}>
                              {option.label}
                            </p>
                          </span>
                        ))
                      ) : (
                        <p className="no-options">No options found</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          {error && <p className="custom-input-error-message">{errorMessage}</p>}
        </div>
      )}
      {version === 'v2' && (
        <div className={`multi-select-dropdown-v2 ${fontSize}`}>
          <div className="multi-select-header">
            <p>
              {title && (
                <p className={`input-label ${error && 'error'}`}>
                  {title}
                  {icon}
                  {valueRequired && <span>*</span>}
                </p>
              )}
            </p>
          </div>
          <div className="multi-select-dropdown-list ">
            <div
              className="dropdown-content "
              ref={reference}
              style={{ borderTop: search ? 'none' : '' }}>
              {search && (
                <div className="multi-select-search">
                  <input
                    type="text"
                    className="dropdown-search"
                    placeholder={t('search')}
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                  />
                  <div className="search-box">
                    <span>
                      <SearchIcon />
                    </span>
                  </div>
                </div>
              )}
              <div className="dropdown-list">
                <div className="dropdown-lists">
                  {sortedOptions.length > 0 ? (
                    sortedOptions.map((option) => (
                      <span key={option.value} className="slide-left">
                        <input
                          style={{ display: multiSelectDisable ? 'none' : '' }}
                          type="checkbox"
                          checked={selectedValues.includes(option.value)}
                          onChange={() => handleCheckboxChange(option.value)}
                        />
                        <p role="presentation" onClick={() => handleCheckboxChange(option.value)}>
                          {option.label}
                        </p>
                      </span>
                    ))
                  ) : (
                    <p className="no-options">{t('noOptionsFound')}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
          {error && <p className="custom-input-error-message">{errorMessage}</p>}
        </div>
      )}
    </>
  );
}
MultiSelectDropdown.defaultProps = {
  title: '',
  placeholder: '',
  valueRequired: false,
  icon: null,
  search: false,
  multiSelectDisable: false,
  fontSize: 'small',
  error: false,
  errorMessage: '',
  version: 'v1',
  disabledAutoSorting: false,
  selectedChips: false,
  limitChips: null,
  disabled: false,
};

export default MultiSelectDropdown;
